/* Copyright (c) 2017 Backpack Health LLC */
import * as _ from 'lodash';

import actionNames from '../../appAuthorized/redux/actionNames';

export const types = {
  UPDATED: 'app/healthEntryValue/UPDATED',
  RECEIVED_FOR_HE: 'app/healthEntryValue/RECEIVED_FOR_HE',
  CREATED_HE_VALUE: 'app/healthEntryValue/CREATED_HE_VALUE',
  DELETED_HE_VALUE: 'app/healthEntryValue/DELETED_HE_VALUE',
  CLEAR_EDITING_HE: 'app/healthEntryValue/CLEAR_EDITING_HE',
  UPDATE_EDITING_HE: 'app/healthEntryValue/UPDATE_EDITING_HE',
};


/*
  byHealthEntry: array of healthEntryValue ids that can be used with the lookup, the key is the healthEntry id
  lookup: heValues by id
*/
const initialState = {
  editing: {},
  valueToHeLookup: {},
  byHealthEntry: {
    '-1': [],
  },
  lookup: {},
};


export default function reducer(state = initialState, action = {}) {
  const newHealthEntries = {};
  let newLookup = {};
  let healthEntryData;
  let heValues;
  const newValueToHeLookup = {};

  switch (action.type) {
    case actionNames.receiveHealthEntry:
      healthEntryData = action.payload.healthEntryData;
      heValues = healthEntryData[healthEntryData.category].values || [];

      heValues.forEach(heValue => {
        newLookup[heValue.id] = heValue;
        newValueToHeLookup[heValue.id] = healthEntryData.id;
      });

      return _.assign({}, state, {
        byHealthEntry: _.assign({}, state.byHealthEntry, {
          [healthEntryData.id]: heValues.map(hev => hev.id),
        }),
        lookup: _.assign({}, state.lookup, newLookup),
        valueToHeLookup: _.assign({}, state.valueToHeLookup, newValueToHeLookup),
      });
    case actionNames.receiveAllHealthEntries:
      action.payload.collection.forEach((healthEntry) => {
        const values = healthEntry[healthEntry.category].values || [];
        newHealthEntries[healthEntry.id] = newHealthEntries[healthEntry.id] || [];
        values.forEach(heValue => {
          newHealthEntries[healthEntry.id].push(heValue.id);
          newLookup[heValue.id] = heValue;
          newValueToHeLookup[heValue.id] = healthEntry.id;
        });
      });
      return _.assign({}, state, {
        valueToHeLookup: _.assign({}, state.valueToHeLookup, newValueToHeLookup),
        byHealthEntry: _.assign({}, state.byHealthEntry, newHealthEntries),
        lookup: _.assign({}, state.lookup, newLookup),
      });
    case types.UPDATED:
      return _.assign({}, state, {
        lookup: _.assign({}, state.lookup, {
          [action.payload.id]: action.payload,
        }),
      });
    case types.RECEIVED_FOR_HE:
      return _.assign({}, state, {
        lookup: _.assign({}, state.lookup, {
          [action.payload.id]: action.payload,
        }),
      });
    case types.CREATED_HE_VALUE:
      return _.assign({}, state, {
        byHealthEntry: _.assign({}, state.byHealthEntry, newHealthEntries),
        lookup: _.assign({}, state.lookup, {
          [action.payload.id]: action.payload,
        }),
      });
    case types.DELETED_HE_VALUE:
      newLookup = _.omit(state.lookup, action.payload.id);

      return _.assign({}, state, {
        byHealthEntry: _.assign({}, state.byHealthEntry, {
          [action.payload.healthEntryId]: state.byHealthEntry[action.payload.healthEntryId].filter(v => v !== action.payload.id),
        }),
        lookup: newLookup,
      });
    case types.CLEAR_EDITING_HE:
      return _.assign({}, state, {
        editing: {},
      });
    case types.UPDATE_EDITING_HE:
      return _.assign({}, state, {
        editing: action.payload,
      });
    default:
      return state;
  }
}


function updated(healthEntryValue) {
  return {
    type: types.UPDATED,
    payload: healthEntryValue,
  };
}

function receivedAllForHealthEntry(healthEntryId, healthEntryValues) {
  return {
    type: types.RECEIVED_FOR_HE,
    payload: {
      healthEntryId,
      healthEntryValues,
    },
  };
}

function createdHealthEntryValue(healthEntryId, healthEntryValue) {
  return {
    type: types.CREATED_HE_VALUE,
    payload: {
      healthEntryId,
      healthEntryValue,
    },
  };
}

function deletedHealthEntryValue(healthEntryId, heValueId) {
  return {
    type: types.DELETED_HE_VALUE,
    payload: {
      healthEntryId,
      id: heValueId,
    },
  };
}

function submitUpdate(heValueData) {
  return (dispatch) => {
    // perform health entry value update api call

    dispatch(updated(heValueData));
  };
}

function requestAllForHealthEntry(healthEntryId) {
  return (dispatch) => {
    // some api call that gets health entry values
    const heValues = [];

    dispatch(receivedAllForHealthEntry(healthEntryId, heValues));
  };
}

function createHealthEntryValue(healthEntryId, heValueData) {
  return (dispatch) => {
    // some api call that creates a health entry value

    dispatch(createdHealthEntryValue(healthEntryId, heValueData));
  };
}

function deleteHealthEntryValue(healthEntryId, heValueId) {
  return (dispatch) => {
    // some api call that deletes a health entry value

    dispatch(deletedHealthEntryValue(healthEntryId, heValueId));
  };
}

function clearEditing() {
  return {
    type: types.CLEAR_EDITING_HE,
    payload: {},
  };
}

function updateEditing(data) {
  return {
    type: types.UPDATE_EDITING_HE,
    payload: data,
  };
}

export const actions = {
  updated,
  submitUpdate,
  requestAllForHealthEntry,
  createHealthEntryValue,
  deleteHealthEntryValue,
  clearEditing,
  updateEditing,
};

export const selectors = {
  getHealthEntryValuesForHealthEntry: (state, healthEntryId) => {
    const heValueIds = state.data.healthEntryValues.byHealthEntry[healthEntryId] || [];
    return heValueIds.map(id => state.data.healthEntryValues.lookup[id]);
  },
  getHealthEntryValue: (state, healthEntryValueId) => (state.data.healthEntryValues.lookup[healthEntryValueId] || {}),
  getEditing: (state, healthEntryValueId) => {
    const heValue = state.data.healthEntryValues.editing.id ? state.data.healthEntryValues.editing : state.data.healthEntryValues.lookup[healthEntryValueId];
    return heValue || {};
  },
  getCategory: (state, profileId, valueId) => {
    const heId = state.data.healthEntryValues.valueToHeLookup[valueId];
    return heId ? _.get(state, `data.healthEntries.${profileId}.lookup.${heId}.category`) : null;
  },
  getHealthEntry: (state, profileId, valueId) => {
    const heId = state.data.healthEntryValues.valueToHeLookup[valueId];
    return _.get(state, `data.healthEntries.${profileId}.lookup.${heId}`, {});
  },
};
